/**
 * Compact play sync. The host simulates. Guests build the same level once, then move the
 * existing objects to the host's positions and draw. They do not step physics.
 */
import type { Game } from './sim/game.ts';
import type { PlayerInput } from './sim/game.ts';
import type { GameEvent, LevelActionKind } from './sim/game.ts';
import { PlayerController, PlayerState } from './sim/player.ts';
import { Coin, Gem, GemShard, PowerupCollectable } from './sim/mechanics/collectables.ts';
import { ColorMatchBlock } from './sim/mechanics/platforms.ts';
import { TheString } from './sim/mechanics/string.ts';
import { KOTHHandler, Volleyball, VolleyballHandler } from './sim/mechanics/modes.ts';
import { BodyType, type SimGo } from './sim/world.ts';
import type { NetBody, NetEvent, NetInput, NetKoth, NetLink, NetPlayer, NetSnap, NetVolley } from '../lobby/protocol.ts';

export interface NetPlay {
  role: 'host' | 'guest';
  /** slot this machine controls, or null to watch */
  slot: number | null;
  slots: (string | null)[];
  names: (string | null)[];
  /** account id of this machine, so it knows which slots it plays */
  self: string;
  onInput?: (slot: number, input: PlayerInput) => void;
  onSnap?: (snap: NetSnap) => void;
  /** claim the next free player slot for this account */
  onTake?: () => void;
  /** guest pause-menu command, relayed to the host */
  onMenu?: (cmd: string, n?: number) => void;
  pullInputs?: () => Map<number, NetInput>;
  pullMenu?: () => { cmd: string; n?: number }[];
  pullSnap?: () => { seq: number; snap: NetSnap } | null;
}

const q = (n: number) => Math.round(n * 100) / 100;

export function toNetInput(input: PlayerInput): NetInput {
  return {
    movement: { x: input.movement.x, y: input.movement.y },
    control: { x: input.control.x, y: input.control.y },
    jumpPressed: input.jumpPressed,
    jumpReleased: input.jumpReleased,
    dashPressed: input.dashPressed,
    interactPressed: input.interactPressed,
    interactDown: input.interactDown,
    powerupPressed: input.powerupPressed,
    powerupReleased: input.powerupReleased,
    restartPressed: false,
    claimCameraPressed: input.claimCameraPressed,
    controlEnabled: input.controlEnabled,
    leftTrigger: input.leftTrigger,
    rightTrigger: input.rightTrigger,
    dUp: input.dUp,
    dDown: input.dDown,
    dLeft: input.dLeft,
    dRight: input.dRight,
    pausePressed: input.pausePressed,
  };
}

export function fromNetInput(input: NetInput): PlayerInput {
  return {
    movement: { x: input.movement.x, y: input.movement.y },
    control: { x: input.control.x, y: input.control.y },
    jumpPressed: input.jumpPressed,
    jumpReleased: input.jumpReleased,
    dashPressed: input.dashPressed,
    interactPressed: input.interactPressed,
    interactDown: input.interactDown,
    powerupPressed: input.powerupPressed,
    powerupReleased: input.powerupReleased,
    restartPressed: false,
    claimCameraPressed: input.claimCameraPressed,
    controlEnabled: input.controlEnabled,
    leftTrigger: input.leftTrigger,
    rightTrigger: input.rightTrigger,
    dUp: input.dUp,
    dDown: input.dDown,
    dLeft: input.dLeft,
    dRight: input.dRight,
    pausePressed: !!input.pausePressed,
  };
}

export function toGameEvent(event: NetEvent): GameEvent | null {
  if (event.kind === 'sound' && event.name && event.x !== undefined && event.y !== undefined) return { kind: 'sound', name: event.name, x: event.x, y: event.y };
  if (event.kind === 'collect' && event.what && event.player !== undefined && event.x !== undefined && event.y !== undefined) {
    return { kind: 'collect', what: event.what as LevelActionKind, player: event.player, x: event.x, y: event.y };
  }
  if (event.kind === 'death' && event.player !== undefined && event.x !== undefined && event.y !== undefined) {
    return { kind: 'death', player: event.player, x: event.x, y: event.y, color: [1, 1, 1, 1] };
  }
  if (event.kind === 'checkpoint' && event.x !== undefined && event.y !== undefined) return { kind: 'checkpoint', x: event.x, y: event.y };
  if (event.kind === 'restart') return { kind: 'restart' };
  if (event.kind === 'levelComplete') return { kind: 'levelComplete' };
  return null;
}

export function fromGameEvent(event: GameEvent): NetEvent {
  if (event.kind === 'sound') return { kind: 'sound', name: event.name, x: event.x, y: event.y };
  if (event.kind === 'collect') return { kind: 'collect', what: event.what, player: event.player, x: event.x, y: event.y };
  if (event.kind === 'death') return { kind: 'death', player: event.player, x: event.x, y: event.y };
  if (event.kind === 'checkpoint') return { kind: 'checkpoint', x: event.x, y: event.y };
  return { kind: event.kind };
}

/** Occupied lobby slots, in order. Player i in the sim is this slot. */
export function occupiedSlots(slots: (string | null)[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < slots.length && out.length < 4; i++) if (slots[i]) out.push(i);
  return out;
}

function pickupGone(b: Coin | Gem | GemShard | PowerupCollectable): boolean {
  return b.isConsumed || b.go.destroyed || !b.go.active;
}

function netId(go: SimGo, entities: Map<string, SimGo>): string {
  let cur: SimGo | null = go;
  while (cur) {
    if (entities.get(cur.key) === cur) return cur.key;
    cur = cur.parent;
  }
  return go.key;
}

function isPickup(b: object): b is Coin | Gem | GemShard | PowerupCollectable {
  return b instanceof Coin || b instanceof Gem || b instanceof GemShard || b instanceof PowerupCollectable;
}

export function captureSnap(game: Game, slotOfPlayer: number[], events: NetEvent[], last: Map<string, string>): NetSnap {
  const players: NetPlayer[] = [];
  game.players.forEach((player, index) => {
    const slot = slotOfPlayer[index];
    if (slot === undefined || !(player instanceof PlayerController)) return;
    const pos = player.go.position;
    players.push({
      slot,
      x: q(pos.x),
      y: q(pos.y),
      a: q(player.go.body?.rotation ?? player.go.rotation),
      state: player.playerState,
      display: player.characterDisplayState,
      coins: player.coinCount,
      deaths: player.totalDeaths,
      hide: player.go.destroyed || !player.go.active,
      dash: player.isDashing(),
      color: [q(player.color[0]), q(player.color[1]), q(player.color[2])],
      fly: player.isUsingDebugControl,
      ...(player.soundLoops ? { loops: player.soundLoops } : {}),
      ...(player.livesLabel ? { lives: player.livesLabel } : {}),
    });
  });
  const bodies: NetBody[] = [];
  for (const [id, go] of game.entities) {
    const body = go.body ?? go.attachedBody();
    const hide = go.destroyed || !go.active;
    if (!body || body.type === BodyType.Static) {
      if (!hide || last.get(id) === 'hide') continue;
      last.set(id, 'hide');
      bodies.push({ id, x: 0, y: 0, a: 0, vx: 0, vy: 0, hide: true });
    } else {
      const pos = body.position;
      const rec: NetBody = { id, x: q(pos.x), y: q(pos.y), a: q(body.rotation), vx: q(body.velocity.x), vy: q(body.velocity.y), hide };
      const key = `${rec.x},${rec.y},${rec.a},${rec.vx},${rec.vy},${hide ? 1 : 0}`;
      if (last.get(id) === key) continue;
      last.set(id, key);
      bodies.push(rec);
    }
    if (bodies.length >= 400) break;
  }
  for (const block of game.behaviours) {
    if (!(block instanceof ColorMatchBlock) || !block.go.sprite || block.go.destroyed) continue;
    const c = block.go.sprite.color;
    const id = block.go.key;
    const tint: [number, number, number, number] = [q(c[0]), q(c[1]), q(c[2]), q(c[3])];
    const key = `tint:${tint.join(',')}`;
    if (last.get(`tint:${id}`) === key) continue;
    last.set(`tint:${id}`, key);
    const already = bodies.find((body) => body.id === id);
    if (already) already.tint = tint;
    else bodies.push({ id, x: 0, y: 0, a: 0, vx: 0, vy: 0, hide: false, tint, tintOnly: true });
  }
  const links: NetLink[] = [];
  let koth: NetKoth[] | undefined;
  let volley: NetVolley | undefined;
  for (const s of game.behaviours) {
    if (s instanceof TheString && !s.go.destroyed && s.player1 && s.player2) links.push({ a: s.player1.index, b: s.player2.index, on: s.isActive });
    if (s instanceof KOTHHandler) {
      koth = s.rows().map((row) => ({
        slot: slotOfPlayer[row.slot] ?? row.slot,
        lives: row.lives,
        text: row.text,
        color: [q(row.color[0]), q(row.color[1]), q(row.color[2])] as [number, number, number],
      }));
    }
    if (s instanceof VolleyballHandler) {
      volley = { left: s.leftScore, right: s.rightScore };
    }
    if (s instanceof Volleyball && volley && !volley.color) {
      const tint = s.tint();
      if (tint) volley.color = [q(tint[0]), q(tint[1]), q(tint[2]), q(tint[3])];
    }
    if (!isPickup(s)) continue;
    const gone = pickupGone(s);
    const id = netId(s.go, game.entities);
    const visKey = `vis:${id}`;
    if (!gone && last.get(visKey) !== 'hide') continue;
    last.set(visKey, gone ? 'hide' : 'show');
    bodies.push({ id, x: 0, y: 0, a: 0, vx: 0, vy: 0, hide: gone, tintOnly: true });
  }
  const cam = game.camera;
  return {
    cam: { x: q(cam?.pos.x ?? 0), y: q(cam?.pos.y ?? 0), size: q(cam?.orthoSize ?? 12) },
    players,
    bodies,
    events: events.splice(0, events.length),
    links,
    paused: game.isPaused ? { debug: game.pauseDebug, slot: game.pauseSlot } : null,
    ...(koth ? { koth } : {}),
    ...(volley ? { volley } : {}),
  };
}

/** Guest copy of king-of-the-hill lives and the volleyball score. */
export function applyModeSnap(game: Game, snap: NetSnap, slotOfPlayer: number[]): void {
  if (snap.paused !== undefined) {
    if (snap.paused) {
      game.isPaused = true;
      game.pauseDebug = snap.paused.debug;
      game.pauseSlot = snap.paused.slot;
    } else game.isPaused = false;
  }
  for (const b of game.behaviours) {
    if (b instanceof KOTHHandler && snap.koth) {
      b.applyNet(snap.koth.map((row) => ({ ...row, slot: slotOfPlayer.indexOf(row.slot) })));
    }
    if (b instanceof VolleyballHandler && snap.volley) b.applyNet(snap.volley);
    if (b instanceof Volleyball && snap.volley?.color) b.applyTint(snap.volley.color);
  }
  for (const sample of snap.players) {
    const player = game.players[slotOfPlayer.indexOf(sample.slot)];
    if (player instanceof PlayerController && sample.lives) player.livesLabel = sample.lives;
  }
}

export function placePlayer(player: PlayerController, sample: NetPlayer, at: { x: number; y: number; a: number }): void {
  if (sample.hide) {
    player.go.setActive(false);
    return;
  }
  if (!player.go.active) player.go.setActive(true);
  player.go.position = { x: at.x, y: at.y };
  if (player.go.body) player.go.body.rotation = at.a;
  player.playerState = sample.state as typeof player.playerState;
  player.characterDisplayState = sample.display as typeof player.characterDisplayState;
  player.coinCount = sample.coins;
  player.totalDeaths = sample.deaths;
  player.color = [sample.color[0], sample.color[1], sample.color[2], 1];
  player.soundLoops = sample.loops ?? 0;
  if (sample.lives) player.livesLabel = sample.lives;
  const inDoor = sample.state === PlayerState.InDoor;
  const visible = sample.state === PlayerState.Playing || inDoor;
  if (player.go.sprite) player.go.sprite.color = [sample.color[0], sample.color[1], sample.color[2], player.go.sprite.color[3]];
  if (player.go.sprite) player.go.sprite.enabled = visible;
  player.updateSprite();
  if (player.go.sprite) {
    if (inDoor) player.go.sprite.order = -1;
    else if (player.go.sprite.order === -1) player.go.sprite.order = 0;
    if (sample.fly) player.go.sprite.sortingLayer = 'This is your brain on drugs';
    else if (player.go.sprite.sortingLayer === 'This is your brain on drugs') player.go.sprite.sortingLayer = 'Player';
  }
}

export function placeBody(entities: Map<string, SimGo>, sample: NetBody, at: { x: number; y: number; a: number }): void {
  const go = entities.get(sample.id);
  if (!go) return;
  if (sample.tint && go.sprite) go.sprite.color = [sample.tint[0], sample.tint[1], sample.tint[2], sample.tint[3]];
  if (sample.hide) {
    go.setActive(false);
    return;
  }
  if (!go.active) go.setActive(true);
  if (sample.tintOnly) return;
  const body = go.body ?? go.attachedBody();
  const carrier = body?.go ?? go;
  carrier.position = { x: at.x, y: at.y };
  if (body && body.type !== BodyType.Static) body.rotation = at.a;
}
